import { clearbuyLinkMapper } from 'data-source/mappers/clearbuyLink'
import { imageMapper } from 'data-source/mappers/imageMapper'
import { ncProductMSRPMapper } from 'data-source/mappers/ncProductMSRPMapper'
import { clearBuyPriceMapper } from 'data-source/mappers/oneDealPriceMapper'
import { optionalLinkMapper } from 'data-source/mappers/optionalLinkMapper'
import { pLinkExternalMapper, PLinkMapperOpts } from 'data-source/mappers/pLinkMapper'
import { productBadgesMapper } from 'data-source/mappers/productBadgesMapper'
import { retailerButtonTitleMapper } from 'data-source/mappers/retailerButtonTitleMapper'
import { ApiClearBuyPrice, ApiClearBuyProduct, ApiProduct } from 'data-source/types.wp'
import { nullableNumber } from 'shared-code/conversion'
import {
  DSDealButton,
  DSNCDealsButton,
  DSNCDealsDetailed,
  DSNCDealsLarge,
  DSNCDealsLatest,
  DSNCDealsLargeAlt,
  DSNCDealsMedium,
  DSNCDealsMediumCarousel,
  DSNCDealsSimple,
  DSTextBadge,
} from 'shared-definitions/types'
import { discountPercentMapper } from './discountPercentMapper'

type Res<T> = T | T[]

type DSNCDealsUnion =
  | DSNCDealsButton
  | DSNCDealsSimple
  | DSNCDealsMedium
  | DSNCDealsDetailed
  | DSNCDealsLarge
  | DSNCDealsMediumCarousel
  | DSNCDealsLatest
  | DSNCDealsLargeAlt

export function ncProduct(input: ApiProduct, opts: PLinkMapperOpts): Res<DSNCDealsUnion> {
  if (input.style === 'button') {
    return input.products.flatMap(item => {
      const buttons = getProductDealButtons(item.prices, opts)

      if (!buttons.length) {
        return []
      }

      return {
        resource: 'nc-deals-button',
        pLinkEdit: optionalLinkMapper(item.edit_url),
        dataAttrs: item.data_attributes ?? {},
        anchor: item.anchor ?? null,
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        buttons,
        msrp: ncProductMSRPMapper(item),
        textBadge: extractExpiredBadge(item),
      }
    })
  }

  if (input.style === 'latest') {
    return {
      resource: 'nc-deals-latest',
      title: input.top_deals_text ?? '',
      button: {
        label: input.top_deals_link_text ?? '',
        pLink: pLinkExternalMapper(input.top_deals_link ?? ''),
      },
      items: input.products.flatMap(product => {
        const image = product.image ? imageMapper({ src: product.image }, opts) : null

        return product.prices.map(price => {
          const { discountPercent } = discountPercentMapper(
            price.current_price,
            price.original_price
          )
          return {
            coupon: price.coupon_code ?? null,
            customText: price.retailer_custom_text ?? null,
            pLinkEdit: optionalLinkMapper(product.edit_url),
            dataAttrs: product.data_attributes ?? {},
            pLinkRequestUpdate: optionalLinkMapper(product.update_request_url),
            title: product.name,
            discountString: discountPercent ? `${discountPercent}% off` : null,
            priceButton: productButtonMapper(price, opts, price.button_custom_text),
            image,
            textBadge: extractExpiredBadge(product),
          }
        })
      }),
    }
  }

  if (input.style === 'simple') {
    return input.products.flatMap(item => {
      const buttons = getProductDealButtons(item.prices, opts)

      if (!buttons.length) {
        return []
      }

      return {
        resource: 'nc-deals-simple',
        dataAttrs: item.data_attributes ?? {},
        pLinkEdit: optionalLinkMapper(item.edit_url),
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        title: item.name,
        anchor: item.anchor ?? null,
        refLink: item.review_url
          ? { label: 'See review', pLink: pLinkExternalMapper(item.review_url) }
          : null,
        buttons,
        msrp: ncProductMSRPMapper(item),
        textBadge: extractExpiredBadge(item),
      }
    })
  }

  if (input.style === 'medium') {
    const items = input.products.flatMap(item => {
      const image = item.image ? imageMapper({ src: item.image }, opts) : null

      if (!image) {
        return []
      }

      // DR-1089 fix
      if (!image.alt) {
        image.alt = item.name
      }
      // DR-1089 fix end

      const buttons = getProductDealButtons(item.prices, opts)

      if (!buttons.length) {
        return []
      }

      return {
        title: item.name,
        pLinkEdit: optionalLinkMapper(item.edit_url),
        dataAttrs: item.data_attributes ?? {},
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        tags: item.reason_to_buy ?? [],
        anchor: item.anchor ?? null,
        image,
        buttons,
        msrp: ncProductMSRPMapper(item),
        textBadge: extractExpiredBadge(item),
      }
    })

    if (items.length) {
      return {
        resource: 'nc-deals-medium',
        items,
      }
    }
    return []
  }

  if (input.style === 'detailed') {
    return input.products.flatMap(item => {
      const buttons = getProductDealButtons(item.prices, opts)

      if (!buttons.length) {
        return []
      }

      return {
        resource: 'nc-deals-detailed',
        dataAttrs: item.data_attributes ?? {},
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        pLinkEdit: optionalLinkMapper(item.edit_url),
        title: item.name,
        tags: item.reason_to_buy ?? [],
        anchor: item.anchor ?? null,
        score: nullableNumber(item.rating) ?? null,
        refLink: item.review_url
          ? { label: 'See review', pLink: pLinkExternalMapper(item.review_url) }
          : null,
        buttons,
        msrp: ncProductMSRPMapper(item),
        textBadge: extractExpiredBadge(item),
      }
    })
  }

  if (input.style === 'large' || input.style === 'large2') {
    return input.products.flatMap(item => {
      const image = item.image ? imageMapper({ src: item.image }, opts) : null

      if (!image) {
        return []
      }

      // DR-1089 fix
      if (!image.alt) {
        image.alt = item.name
      }
      // DR-1089 fix end

      const buttons =
        input.style === 'large2'
          ? getProductDealButtons(item.prices.slice(0, 2), opts)
          : getProductDealButtons(item.prices, opts)

      if (!buttons.length) {
        return []
      }

      return {
        abTest: input.style === 'large2' ? 'grid' : 'accordion',
        resource: 'nc-deals-large',
        dataAttrs: item.data_attributes ?? {},
        anchor: item.anchor ?? null,
        pLinkEdit: optionalLinkMapper(item.edit_url),
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        title: item.name,
        subtitle: item.excerpt || item.tagline,
        brand: item.brand ?? null,
        score: nullableNumber(item.rating),
        tags: item.reason_to_buy ?? [],
        image,
        text: item.summary,
        buttons,
        badges: productBadgesMapper(item.badges, opts),
        msrp: ncProductMSRPMapper(item),
        textBadge: extractExpiredBadge(item),
      }
    })
  }

  if (input.style === 'best-list-large') {
    return input.products.flatMap(item => {
      const image = item.image ? imageMapper({ src: item.image }, opts) : null
      if (!image) {
        return []
      }

      const buttons = getProductDealButtons(item.prices.slice(0, 3), opts)

      if (!buttons.length) {
        return []
      }

      return {
        image,
        resource: 'nc-deals-large-alt',
        dataAttrs: item.data_attributes ?? {},
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        pLinkEdit: optionalLinkMapper(item.edit_url),
        title: item.name,
        buttons,
        tags: item.reason_to_buy ?? [],
        subtitle: item.excerpt || item.tagline,
        score: nullableNumber(item.rating),
        msrp: ncProductMSRPMapper(item),
        badges: productBadgesMapper(item.badges, opts),
        text: item.summary,
        features: {
          positive: item.positives
            ? {
                title: 'Positives',
                list: item.positives,
              }
            : null,
          negative: item.negatives
            ? {
                title: 'Cons',
                list: item.negatives,
              }
            : null,
        },
      }
    })
  }

  if (input.style === 'medium-carousel') {
    const items = input.products.flatMap(item => {
      const image = item.image ? imageMapper({ src: item.image }, opts) : null

      if (!image) {
        return []
      }

      // DR-1089 fix
      if (!image.alt) {
        image.alt = item.name
      }
      // DR-1089 fix end

      const buttons = getProductDealButtons(item.prices, opts)

      if (!buttons.length) {
        return []
      }

      return {
        title: item.name,
        pLinkEdit: optionalLinkMapper(item.edit_url),
        dataAttrs: item.data_attributes ?? {},
        pLinkRequestUpdate: optionalLinkMapper(item.update_request_url),
        tags: item.reason_to_buy ?? [],
        anchor: item.anchor ?? null,
        image,
        msrp: ncProductMSRPMapper(item),
        buttons,
        textBadge: extractExpiredBadge(item),
      }
    })

    if (items.length) {
      return {
        resource: 'nc-deals-medium-carousel',
        items,
      }
    }
    return []
  }

  return []
}

function getProductDealButtons(prices: ApiClearBuyPrice[], opts: PLinkMapperOpts): DSDealButton[] {
  return prices.flatMap(price => {
    const button = productButtonMapper(price, opts, price.button_custom_text)
    if (!button) {
      return []
    }
    return button
  })
}

function extractExpiredBadge(input: ApiClearBuyProduct): DSTextBadge | null {
  if (input.expired && input.expires) {
    return {
      text: input.expires,
      style: 'warning',
    }
  }
  return null
}

function productButtonMapper(
  input: ApiClearBuyPrice,
  opts: PLinkMapperOpts,
  customLabel?: string | null
): DSDealButton | null {
  if (!input.url) {
    return null
  }

  return {
    link: {
      label:
        customLabel ??
        retailerButtonTitleMapper(input.agent_name, {
          is_free: input.is_free === 1 || input.is_free === true,
          current_price: input.current_price,
          currency: input.currency ?? undefined,
        }),
      pLink: clearbuyLinkMapper(input.url, opts),
    },
    price: clearBuyPriceMapper(input),
  }
}
